import React, { useState } from "react";
import { Clock, AlertTriangle, Phone, Voicemail, ChevronDown, ChevronUp } from "lucide-react";
import { avatarColor, avatarInitials } from './avatarUtils';
import CallButton from "../clicktocall/CallButton";
import LogicalCallPanel from "./LogicalCallPanel";
import useCompanyMembers, { resolveOperatorName } from "../../hooks/useCompanyMembers";
import { useCompany } from "../../context/CompanyContext";

// Phase 2.8.B / 4.3.A — collapsed transcript reveal under the voicemail
// or call-recording audio player. Default state: collapsed
// ("View transcription"). Read-only; never blocks playback or reply.
function TranscriptToggle({ message, tone = 'amber' }) {
  const [open, setOpen] = useState(false);
  const status = message.transcriptionStatus || 'pending';
  const text = (message.transcription || '').trim();

  // Only render once the transcript actually lands. Until then the bubble
  // stays clean — the affordance appears via Firestore's real-time
  // listener; no manual refresh needed.
  if (status !== 'ready' || !text) return null;

  const toneCls = tone === 'emerald'
    ? {
        border: 'border-emerald-200/70',
        btn:    'text-emerald-800 hover:text-emerald-900',
        text:   'text-emerald-950',
      }
    : {
        border: 'border-amber-200/70',
        btn:    'text-amber-800 hover:text-amber-900',
        text:   'text-amber-950',
      };

  return (
    <div className={`mt-2 border-t ${toneCls.border} pt-1.5`} data-testid="voicemail-transcript">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="voicemail-transcript-toggle"
        aria-expanded={open}
        className={`inline-flex items-center gap-1 text-[11px] font-medium ${toneCls.btn}`}
      >
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {open ? 'Hide transcription' : 'View transcription'}
      </button>
      {open && (
        <div
          data-testid="voicemail-transcript-text"
          className={`mt-1.5 text-[12px] leading-relaxed ${toneCls.text} whitespace-pre-wrap`}
        >
          {text}
        </div>
      )}
    </div>
  );
}

// Backwards-compat alias — the old VoicemailTranscript used elsewhere
// in this file continues to work unchanged.
const VoicemailTranscript = TranscriptToggle;


/**
 * Message bubble component.
 *
 * Alignments:
 *   - Client (inbound):  left,  gray bubble
 *   - Outbound (staff):  right, blue bubble
 *   - Internal/system:   center, yellow note
 *
 * Sender attribution:
 *   - Outbound messages include sentByName under the bubble.
 *   - If sentByUserId matches the logged-in user, shows "You".
 *   - If a different team member sent it, shows their name.
 *   - Falls back to "You" for legacy messages without sentByUserId.
 */
export default function MessageBubble({ message, currentUser, conversationId, clientPhone }) {
  // Resolve operator display names so call-event pills show "Will"
  // instead of "wbatts47@gmail.com". Source of truth is the same
  // `/companies/{companyId}/users` collection that powers the Dispatch
  // board's technician columns (see `useCompanyTechnicians`). The hook
  // is called unconditionally to satisfy Rules of Hooks — the early
  // `return null` below happens AFTER it resolves.
  const { companyId } = useCompany() || {};
  const { membersById } = useCompanyMembers(companyId);

  if (!message) return null;

  const isInternal     = message.type === "internal";
  const isQuoteCreated = isInternal && message.kind === 'quote_created';
  const isQuoteSent    = isInternal && message.kind === 'quote_sent';
  const isMissedCall   = isInternal && message.kind === 'missed_call';
  const isManualCall   = isInternal && message.kind === 'call_started_manual';
  const isWebrtcStart  = isInternal && message.kind === 'call_started_webrtc';
  const isWebrtcEnd    = isInternal && message.kind === 'call_ended_webrtc';
  // Phone-bridge calls (clicktocall_service) write the same event kinds
  // without the `_webrtc` suffix. Treat them as the same UI pill so
  // users don't see a difference based on transport.
  const isBridgeStart  = isInternal && message.kind === 'call_started';
  const isBridgeEnd    = isInternal && message.kind === 'call_ended';
  const isCallRecording = isInternal && message.kind === 'call_recording_webrtc';
  const isVoicemail    = message.type === "voicemail";
  const isVoicemailDeleted = message.type === "voicemail_deleted";
  const isClient       = !isInternal && !isVoicemail && !isVoicemailDeleted && !isCallRecording && message.from === "client";
  const isOutbound     = !isInternal && !isVoicemail && !isVoicemailDeleted && !isCallRecording && !isClient;
  const deliveryStatus = isOutbound ? (message.deliveryStatus || 'sent') : null;

  // Sender attribution
  const sentByCurrentUser =
    !message.sentByUserId || message.sentByUserId === currentUser?.uid;
  const senderDisplayName = sentByCurrentUser
    ? (currentUser?.displayName || 'You')
    : (message.sentByName || 'Team');
  const senderLabel = sentByCurrentUser ? 'You' : senderDisplayName;

  // Avatar
  const avatarName    = sentByCurrentUser
    ? (currentUser?.displayName || 'You')
    : senderDisplayName;
  const avatarBg      = avatarColor(avatarName);
  const avatarLetters = avatarInitials(avatarName);
  const avatarImg     = sentByCurrentUser
    ? (currentUser?.photoURL || currentUser?.avatarUrl || null)
    : null;

  // Timestamp
  let formattedTime = "";
  try {
    const ts = message.timestamp?.toDate
      ? message.timestamp.toDate()
      : message.timestamp ? new Date(message.timestamp) : null;
    if (ts) formattedTime = ts.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch (_) { /* non-critical */ }

  // Media
  const mediaUrls = Array.isArray(message.media)
    ? message.media
    : message.mediaUrl ? [message.mediaUrl] : [];

  // ── Missed-call system pill ───────────────────────────────────────────────
  if (isMissedCall) {
    let mcTime = '';
    try {
      const ts = message.timestamp?.toDate
        ? message.timestamp.toDate()
        : message.timestamp ? new Date(message.timestamp) : null;
      if (ts) mcTime = ts.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch (_) { /* non-critical */ }

    const fromPhone = message.fromPhone || '';
    const cause = (message.hangupCause || '').replace(/_/g, ' ');
    const auto = message.automation === 'missed_call_text_back';

    return (
      <div className="w-full flex justify-center my-2 text-xs" data-testid="missed-call-event">
        <div className="inline-flex flex-col items-center px-3 py-2 rounded-full bg-amber-50 text-amber-900 border border-amber-200 max-w-[80%] shadow-sm">
          <div className="flex items-center gap-1.5 font-medium">
            <Phone className="w-3.5 h-3.5" />
            <span>Missed call{fromPhone ? ` from ${fromPhone}` : ''}</span>
          </div>
          {(cause || mcTime) && (
            <div className="mt-0.5 text-[11px] text-amber-700/80">
              {cause}{cause && mcTime ? ' · ' : ''}{mcTime}
            </div>
          )}
          {auto && (
            <div className="mt-0.5 text-[11px] text-amber-700/80">Auto-reply sent</div>
          )}
          {(fromPhone || clientPhone) && (
            <CallButton
              phone={fromPhone || clientPhone}
              conversationId={conversationId}
              surface="missed-call-bubble"
              title="Call back"
              data-testid="missed-call-callback-btn"
              className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-900 text-[11px] font-medium transition-colors"
            >
              <Phone className="w-3 h-3" />
              Call back
            </CallButton>
          )}
        </div>
      </div>
    );
  }

  // ── Manual outbound-call pill (Phase 3.1B native fallback) ─────────────────
  if (isManualCall) {
    let mTime = '';
    try {
      const ts = message.timestamp?.toDate
        ? message.timestamp.toDate()
        : message.timestamp ? new Date(message.timestamp) : null;
      if (ts) mTime = ts.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch (_) { /* non-critical */ }

    const meta = message.metadata || {};
    const who = resolveOperatorName({
      uid: meta.uid, email: meta.userEmail, membersById,
    });
    const target = meta.customerPhone || '';

    return (
      <div className="w-full flex justify-center my-2 text-xs" data-testid="manual-call-event">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-900 border border-blue-200 max-w-[80%] shadow-sm">
          <Phone className="w-3.5 h-3.5" />
          <span className="font-medium">Called {target}</span>
          <span className="text-blue-700/70">· {who}</span>
          {mTime && <span className="text-blue-700/60">· {mTime}</span>}
        </div>
      </div>
    );
  }

  // ── Call start/end pills ─────────────────────────────────────────────
  if (isWebrtcStart || isWebrtcEnd || isBridgeStart || isBridgeEnd) {
    let wTime = '';
    try {
      const ts = message.timestamp?.toDate
        ? message.timestamp.toDate()
        : message.timestamp ? new Date(message.timestamp) : null;
      if (ts) wTime = ts.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch (_) { /* non-critical */ }

    const meta = message.metadata || {};
    const who = resolveOperatorName({
      uid: meta.uid, email: meta.userEmail, membersById,
    });
    const target = meta.customerPhone || '';
    const isStart = isWebrtcStart || isBridgeStart;
    const dur = Number(meta.durationSecs || 0);
    const mins = Math.floor(dur / 60);
    const secs = dur % 60;
    const durStr = dur > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : '';
    const colorBase = isStart
      ? 'bg-emerald-50 text-emerald-900 border-emerald-200'
      : 'bg-slate-50 text-slate-700 border-slate-200';
    const accent = isStart ? 'text-emerald-700/80' : 'text-slate-500';
    // Plain-English labels — no transport terminology surfaces to users.
    const startLabel = target ? `Call started → ${target}` : 'Call started';
    let endLabel;
    if (meta.wasBridged === false) {
      endLabel = 'Couldn’t connect';
    } else if (durStr) {
      endLabel = `Call ended · ${durStr}`;
    } else {
      endLabel = 'Call ended';
    }

    // Phase 2 — inline recording + transcript panel when a LogicalCall
    // is present. Historical (pre-Phase-1) bubbles have no
    // `logicalCallId` and render exactly as before.
    const logicalCallId = message.logicalCallId
      || meta.logicalCallId
      || '';

    return (
      <div className="w-full flex justify-center my-2 text-xs" data-testid={isStart ? 'call-started-event' : 'call-ended-event'}>
        <div className={`inline-flex flex-col items-stretch gap-0.5 px-3 py-1.5 rounded-2xl border max-w-[85%] shadow-sm ${colorBase}`}>
          <div className="inline-flex items-center gap-1.5 justify-center">
            <Phone className="w-3.5 h-3.5" />
            <span className="font-medium">
              {isStart ? startLabel : endLabel}
            </span>
            {who && <span className={accent}>· {who}</span>}
            {wTime && <span className={accent}>· {wTime}</span>}
          </div>
          {!isStart && meta.recordingStatus === 'failed' && (
            <div
              className="text-[11px] text-slate-500/80 italic text-center"
              data-testid="recording-unavailable-hint"
            >
              Recording unavailable
            </div>
          )}
          {!isStart && logicalCallId && (
            <LogicalCallPanel logicalCallId={logicalCallId} />
          )}
        </div>
      </div>
    );
  }

  // ── Call recording bubble (Phase 4.3.A) ──────────────────────────────
  if (isCallRecording) {
    let crTime = '';
    try {
      const ts = message.timestamp?.toDate
        ? message.timestamp.toDate()
        : message.timestamp ? new Date(message.timestamp) : null;
      if (ts) crTime = ts.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch (_) { /* non-critical */ }

    const dur = Number(message.durationSecs || 0);
    const mins = Math.floor(dur / 60);
    const secs = dur % 60;
    const durStr = dur > 0
      ? (mins ? `${mins}m ${String(secs).padStart(2, '0')}s` : `${secs}s`)
      : '';
    const audioUrl = message.audioUrl || '';
    const status = message.transcriptionStatus || 'pending';

    return (
      <div className="w-full flex justify-start mb-3 animate-fadeIn" data-testid="call-recording-bubble">
        <div className="max-w-[75%] bg-emerald-50 border border-emerald-200 rounded-xl rounded-bl-none shadow-sm p-3">
          <div className="flex items-center gap-2 text-xs font-medium text-emerald-900 mb-2">
            <Phone className="w-3.5 h-3.5" />
            <span>Call recording</span>
            {durStr && <span className="text-emerald-700/80">· {durStr}</span>}
          </div>
          {audioUrl ? (
            <audio
              controls
              preload="metadata"
              src={audioUrl}
              className="w-full max-w-xs"
              data-testid="call-recording-audio"
            />
          ) : (
            <div className="text-xs text-emerald-700/80" data-testid="call-recording-processing">
              Processing recording…
            </div>
          )}
          {audioUrl && status === 'pending' && (
            <div className="mt-1.5 text-[11px] text-emerald-700/80" data-testid="call-recording-transcribing">
              ⟳ Transcribing…
            </div>
          )}
          {audioUrl && status === 'failed' && (
            <div
              className="mt-1.5 text-[11px] text-emerald-700/60 italic"
              data-testid="call-recording-transcription-unavailable"
            >
              Transcription unavailable
            </div>
          )}
          <TranscriptToggle message={message} tone="emerald" />
          <div className="mt-1.5 flex items-center justify-end">
            <span className="text-[11px] text-emerald-700/70">{crTime}</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Voicemail bubble (Phase 2.7) ─────────────────────────────────────────
  if (isVoicemail) {
    let vmTime = '';
    try {
      const ts = message.timestamp?.toDate
        ? message.timestamp.toDate()
        : message.timestamp ? new Date(message.timestamp) : null;
      if (ts) vmTime = ts.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch (_) { /* non-critical */ }

    const dur = Number(message.durationSecs || 0);
    const audioUrl = message.audioUrl || '';
    const fromPhone = message.fromPhone || '';

    // Inline "Reply" CTA — dispatches a window event that ChatInput
    // listens to for prefill+focus, and ChatWindow listens to for
    // scroll-to-bottom. No modal, no new bubble, no thread change.
    const handleReply = () => {
      try {
        window.dispatchEvent(new CustomEvent('kro:voicemail-reply', {
          detail: { text: 'Hi, we just listened to your voicemail and wanted to follow up.' }
        }));
      } catch (_) { /* older browsers fallback — swallow */ }
    };

    return (
      <div className="w-full flex justify-start mb-3 animate-fadeIn" data-testid="voicemail-bubble">
        <div className="max-w-[75%] bg-amber-50 border border-amber-200 rounded-xl rounded-bl-none shadow-sm p-3">
          <div className="flex items-center gap-2 text-xs font-medium text-amber-900 mb-2">
            <Voicemail className="w-3.5 h-3.5" />
            <span>Voicemail{fromPhone ? ` from ${fromPhone}` : ''}</span>
            <span className="text-amber-700/80">· {dur}s</span>
          </div>
          {audioUrl ? (
            <audio
              controls
              preload="metadata"
              src={audioUrl}
              className="w-full max-w-xs"
              data-testid="voicemail-audio"
            />
          ) : (
            <div className="text-xs text-amber-700/80">Audio not available yet — check back shortly.</div>
          )}
          {/* Phase 2 — compact voicemail preview (only when actual
              customer speech is available). Never fabricates a
              transcript; renders only when the real one is ready. */}
          {(message.transcriptionStatus === 'ready'
            && (message.transcription || '').trim()) && (
            <div
              className="mt-1.5 text-[12px] italic text-amber-900/80 line-clamp-2"
              data-testid="voicemail-preview"
              title={(message.transcription || '').trim()}
            >
              “{(message.transcription || '').trim().slice(0, 140)}
              {((message.transcription || '').trim().length > 140) ? '…' : ''}”
            </div>
          )}
          <VoicemailTranscript message={message} />
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleReply}
                data-testid="voicemail-reply-btn"
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md text-amber-800 hover:text-amber-900 hover:bg-amber-100 transition-colors"
                aria-label="Reply to this voicemail"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
                  <polyline points="9 17 4 12 9 7" />
                  <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                </svg>
                Reply
              </button>
              {(fromPhone || clientPhone) && (
                <CallButton
                  phone={fromPhone || clientPhone}
                  conversationId={conversationId}
                  surface="voicemail-bubble"
                  title="Call back"
                  data-testid="voicemail-callback-btn"
                  className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md text-amber-800 hover:text-amber-900 hover:bg-amber-100 transition-colors"
                >
                  <Phone className="w-3 h-3" />
                  Call back
                </CallButton>
              )}
            </div>
            <span className="text-[11px] text-amber-700/70">{vmTime}</span>
          </div>
        </div>
      </div>
    );
  }

  if (isVoicemailDeleted) {
    return (
      <div className="w-full flex justify-center my-2 text-xs" data-testid="voicemail-deleted">
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
          <Voicemail className="w-3.5 h-3.5" />
          <span>Voicemail deleted</span>
        </div>
      </div>
    );
  }

  // ── Quote system messages ─────────────────────────────────────────────────
  if (isQuoteCreated || isQuoteSent) {
    const amount = typeof message.total === 'number' ? message.total : 0;
    const label  = isQuoteCreated ? 'Quote created' : 'Quote sent';
    const icon   = isQuoteCreated ? '📄' : '📤';

    return (
      <div className="w-full flex justify-center my-2 text-xs">
        <div className="inline-flex flex-col items-center px-3 py-2 rounded-full bg-gray-100 text-gray-700 max-w-[80%] shadow-sm">
          <div className="flex items-center gap-1 mb-1">
            <span>{icon}</span>
            <span className="font-medium">{label} — ${amount.toFixed(2)}</span>
          </div>
          {message.quoteId && (
            <button
              type="button"
              onClick={() => message.publicUrl && window.open(message.publicUrl, '_blank', 'noopener,noreferrer')}
              className="text-[11px] text-indigo-600 hover:underline"
            >
              View Quote
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Normal message bubble ─────────────────────────────────────────────────
  return (
    <div className={`w-full flex mb-3 animate-fadeIn ${isClient ? "justify-start" : "justify-end"}`}>

      {/* Avatar — outbound only, left side of bubble */}
      {isOutbound && !isInternal && (
        <div className="flex items-start mr-2 flex-shrink-0">
          {avatarImg ? (
            <img
              src={avatarImg}
              alt={avatarName}
              className="w-7 h-7 rounded-full object-cover shadow"
            />
          ) : (
            <div
              className="w-7 h-7 rounded-full text-white flex items-center justify-center text-[11px] font-semibold shadow"
              style={{ backgroundColor: avatarBg }}
            >
              {avatarLetters}
            </div>
          )}
        </div>
      )}

      {/* Bubble + meta */}
      <div className="max-w-[75%]">
        <div className={`
          px-4 py-3 rounded-xl shadow-sm
          ${isInternal
            ? "bg-yellow-50 text-gray-900 border border-yellow-200"
            : isClient
              ? "bg-gray-200 text-gray-900 rounded-bl-none"
              : "bg-blue-600 text-white rounded-br-none"
          }
        `}>
          {/* Text */}
          {(message.body || message.text) ? (
            <p className="whitespace-pre-wrap leading-relaxed">
              {message.body || message.text}
            </p>
          ) : null}

          {/* Media */}
          {mediaUrls.length > 0 && (
            <div className="mt-2 space-y-2">
              {mediaUrls.map((url, i) => (
                <img key={i} src={url} alt="attachment"
                  className="rounded-lg max-h-64 object-cover border border-black/10" />
              ))}
            </div>
          )}
        </div>

        {/* Timestamp + sender attribution */}
        <div className={`mt-1 flex items-center gap-1.5 text-xs text-gray-400 ${isClient ? "justify-start" : "justify-end"}`}>
          {isInternal ? (
            <span>Internal note</span>
          ) : (
            <>
              <span>{formattedTime}</span>
              {isOutbound && (
                <span className={`font-medium ${sentByCurrentUser ? '' : 'text-gray-600'}`}>
                  · {senderLabel}
                </span>
              )}
            </>
          )}
        </div>

        {/* Delivery status — outbound only, hidden once 'sent' */}
        {isOutbound && deliveryStatus !== 'sent' && (
          <div className={`flex items-center gap-1 justify-end mt-0.5 text-[11px] ${
            deliveryStatus === 'failed' ? 'text-red-500' : 'text-amber-500'
          }`}>
            {deliveryStatus === 'pending' && (
              <><Clock className="w-3 h-3" /><span>Sending…</span></>
            )}
            {deliveryStatus === 'failed' && (
              <><AlertTriangle className="w-3 h-3" /><span>Failed to send</span></>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
